import { DataField } from "@iventis/domain-model/model/dataField";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { RelativeEventRule } from "@iventis/domain-model/model/relativeEventRule";
import { TimeSpan } from "@iventis/types";
import { RelativeEventRuleRelationship } from "@iventis/domain-model/model/relativeEventRuleRelationship";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { StartTimeType } from "@iventis/domain-model/model/startTimeType";
import { EMPTY_GUID, setTimeOnEventDay, ZERO_TIME_SPAN, dataFieldIdToValueMapping } from "@iventis/utilities";
import { addDays } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { findHierarchyEntityById } from "./hierarchy.helpers";
import { getStartAndEndAtStringDigits, SortEventBlocksByStartAt, splitTimeToSingle } from "./schedule-helper-functions";
import { calculateEndTime } from "./formik-components/duration-input";
import { getDefaultRelativeToValue } from "./time-rules.helpers";
import { TimeType } from "./time-rules.types";
import { getPotentialRelativeEventBlocks } from "./sub-event-relative.helpers";
import { SubEventValues } from "./sub-event.types";

export const DEFAULT_OFFSET_OFF_SESSION: TimeSpan = "00:00:00";
export const DEFAULT_OFFSET_OFF_SCHEDULED_ITEM: TimeSpan = "00:10:00";
export const DEFAULT_DURATION: TimeSpan = "00:30:00";

/** Generates initial values based on an existing event block  */
export const getNextScheduledItemsInitialValues = (eventBlockHierarchy: Hierarchy<EventBlock>, id: string): Partial<SubEventValues> => {
    const sourceEventBlock = findHierarchyEntityById(eventBlockHierarchy?.children, id) as Hierarchy<EventBlock>;
    if (sourceEventBlock == null) {
        return null;
    }
    const previousRelativeEventRule = sourceEventBlock.item.relativeEventRules?.[0];
    return {
        dataFieldValues: dataFieldIdToValueMapping(sourceEventBlock.item.dataFieldValues),
        venueLocation: sourceEventBlock.item.venueLocation,
        estimatedTime: sourceEventBlock.item.estimatedTime,
        relativeEventRules: [
            {
                id: EMPTY_GUID,
                // If the previous scheduled item related to the session, the offset should be reset to default
                offset:
                    previousRelativeEventRule?.relativeToEventBlockTypeId === eventBlockHierarchy.item.type.id
                        ? DEFAULT_OFFSET_OFF_SESSION
                        : previousRelativeEventRule?.offset || DEFAULT_OFFSET_OFF_SCHEDULED_ITEM,
                duration: previousRelativeEventRule?.duration || DEFAULT_DURATION,
                relativeTo: getDefaultRelativeToValue(sourceEventBlock.item.type),
                relationship: RelativeEventRuleRelationship.Specific,
                relativeToEventBlockTypeId: sourceEventBlock.item.type.id,
                relativeToSpecificEventBlock: { id: sourceEventBlock.item.id } as EventBlock,
                recurringEventRules: [],
            } as RelativeEventRule,
        ],
    };
};

export const defaultInitialValues = (
    timezone: string,
    initialValueParams?: { existingEvent: Hierarchy<EventBlock>; parentEvent: Hierarchy<EventBlock> }
): Partial<SubEventValues> => {
    const { existingEvent, parentEvent } = initialValueParams;
    if (existingEvent) {
        const { venueLocation, startTimeType, estimatedTime, relativeEventRules } = existingEvent.item;
        const { startTime, endTime } = getStartAndEndAtStringDigits(existingEvent.item, timezone);
        return {
            venueLocation,
            startTime,
            endTime,
            startTimeType,
            relativeEventRules,
            timeType: relativeEventRules?.length > 0 ? TimeType.Relative : TimeType.Fixed,
            estimatedTime,
        };
    }
    const { startTime } = getStartAndEndAtStringDigits(parentEvent.item, timezone);
    const duration = 5;
    // calcuate end time based on duration
    const endTime = calculateEndTime(startTime, duration);
    const endTimeDate = splitTimeToSingle(endTime.endHours, endTime.endMins, endTime.endSeconds);
    const potentialRelativeEventBlocks = getPotentialRelativeEventBlocks(parentEvent, null).sort((a, b) => SortEventBlocksByStartAt(a as EventBlock, b as EventBlock));
    const latestPotentialRelativeEventBlock = potentialRelativeEventBlocks[potentialRelativeEventBlocks.length - 1];
    return {
        venueLocation: null,
        startTime,
        endTime: endTimeDate,
        duration,
        startTimeType: StartTimeType.NotBefore,
        estimatedTime: true,
        timeType: TimeType.Relative,
        relativeEventRules: [
            {
                id: EMPTY_GUID,
                offset: latestPotentialRelativeEventBlock.type.calendarLevel === 1 ? DEFAULT_OFFSET_OFF_SESSION : DEFAULT_OFFSET_OFF_SCHEDULED_ITEM, // if session
                duration: DEFAULT_DURATION,
                relativeTo: getDefaultRelativeToValue(latestPotentialRelativeEventBlock.type),
                relationship: RelativeEventRuleRelationship.Specific,
                relativeToEventBlockTypeId: latestPotentialRelativeEventBlock.type.id,
                relativeToSpecificEventBlock: { id: latestPotentialRelativeEventBlock.id } as EventBlock,
                recurringEventRules: [],
            } as RelativeEventRule,
        ],
    };
};

export const convertSubEventFormValuesToEventBlock = (
    { dataFieldValues, startTime, endTime, venueLocation, startTimeType, estimatedTime, timeType, relativeEventRules }: SubEventValues,
    type: ScheduleConfigEventBlockType,
    dataFields: DataField[],
    sessionStartDate: Date,
    parentEvent: EventBlock,
    timezone: string,
    id?: string
): Hierarchy<EventBlock> => {
    const startAndEnd = getScheduledItemStartAndEndTimes(sessionStartDate, timezone, startTime, endTime);
    return {
        item: {
            id,
            venueLocation,
            parentId: parentEvent.id,
            scheduleVersionId: parentEvent.scheduleVersionId,
            startAt: startAndEnd.startAt,
            endAt: startAndEnd.endAt,
            type,
            dataFieldValues: Object.keys(dataFieldValues).map((dataField) => ({ dataField: dataFields.find((df) => df.id === dataField), value: dataFieldValues[dataField] })),
            startTimeType,
            estimatedTime,
            relativeEventRules: timeType === TimeType.Relative ? relativeEventRules : null,
        } as EventBlock,
        children: [],
    };
};

// Determines which date to attach to the start and end times of a schedule item
// based on the start time of the session itself and that it must occur after it
export const getScheduledItemStartAndEndTimes = (sessionStart: Date, timezone: string, startTime = ZERO_TIME_SPAN, endTime = ZERO_TIME_SPAN): { startAt: Date; endAt: Date } => {
    if (!sessionStart) {
        throw new Error("Cannot set scheduled item start and end times on an undefined date");
    }

    // Get the session start time in its intended timezone, so we can find midnight
    const sessionStartInTimezone = utcToZonedTime(sessionStart, timezone);
    const midnight = new Date(sessionStartInTimezone.getFullYear(), sessionStartInTimezone.getMonth(), sessionStartInTimezone.getDate());
    // Convert it back to UTC for use in API
    const midnightInUtc = zonedTimeToUtc(midnight, timezone);
    // Determine the schedule item start time
    const scheduledItemStartAt = setTimeOnEventDay(midnightInUtc, startTime, timezone);
    if (scheduledItemStartAt < sessionStart) {
        addDays(scheduledItemStartAt, 1);
    }
    // Determine the schedule item end time
    const scheduledItemEndAt = setTimeOnEventDay(midnightInUtc, endTime, timezone);
    if (scheduledItemEndAt < scheduledItemStartAt) {
        addDays(scheduledItemEndAt, 1);
    }
    return { startAt: scheduledItemStartAt, endAt: scheduledItemEndAt };
};
